import cart from "./cart";
import store from "./store";
import { nextTick } from "vue";
import {
  collectionProductIdSet,
  GWP_MAX_LINE_QUANTITY,
  getGwpConfigs,
  giftLinesForConfig,
  gwpConfigKey,
  isFreeProductProperty,
  isGwpCustomerExcluded,
  totalGiftQuantity,
  type GwpConfigEntry,
} from "./gwp-config";

function qualifyingQuantity(
  cartItems: any[],
  giftVariantId: string,
  collectionProductIds: Set<string>
): number {
  return cartItems.reduce((sum, item) => {
    if (!item || String(item.variant_id) === giftVariantId) {
      return sum;
    }
    if (!collectionProductIds.has(String(item.product_id))) {
      return sum;
    }
    return sum + (Number(item.quantity) || 0);
  }, 0);
}

function parseTrigger(cfg: GwpConfigEntry): number {
  const trigger = parseInt(String(cfg.quantity_trigger ?? "1"), 10);
  if (!Number.isFinite(trigger) || trigger < 1) {
    return 1;
  }
  return trigger;
}

/**
 * Prefer cart_price_trigger_cents from Liquid.
 * Fallbacks:
 * - cart_price_trigger >= 1000 and integer → already cents
 * - otherwise treat cart_price_trigger as major currency units (dollars)
 */
function parsePriceTriggerCents(cfg: GwpConfigEntry): number {
  const centsRaw = parseFloat(String(cfg.cart_price_trigger_cents ?? ""));
  if (Number.isFinite(centsRaw) && centsRaw > 0) {
    return Math.round(centsRaw);
  }

  const raw = parseFloat(String(cfg.cart_price_trigger ?? "0"));
  if (!Number.isFinite(raw) || raw <= 0) {
    return 0;
  }

  // Heuristic: large whole numbers from money/number fields are often already cents
  if (Number.isInteger(raw) && raw >= 1000) {
    return raw;
  }

  return Math.round(raw * 100);
}

function lineSpendCents(item: any): number {
  if (!item) {
    return 0;
  }
  if (typeof item.final_line_price === "number") {
    return item.final_line_price;
  }
  if (typeof item.line_price === "number") {
    return item.line_price;
  }
  return 0;
}

/**
 * Cart spend for GWP price checks — excludes free-gift lines so a gift
 * already in the cart cannot keep the total above the threshold forever.
 */
function cartSpendExcludingGiftsCents(cartItems: any[]): number {
  return cartItems.reduce((sum, item) => {
    if (!item) {
      return sum;
    }
    if (isFreeProductProperty(item.properties?.["_free-product"])) {
      return sum;
    }
    // Also skip known gift variant lines that are free (price/final = 0)
    if (
      isGwpGiftVariantLine(item) &&
      lineSpendCents(item) === 0
    ) {
      return sum;
    }
    return sum + lineSpendCents(item);
  }, 0);
}

function isGwpGiftVariantLine(item: any): boolean {
  if (!item?.variant_id) {
    return false;
  }
  return getGwpConfigs().some(
    (cfg) => String(cfg.variant_id) === String(item.variant_id)
  );
}

function qualifiesForGift(
  cfg: GwpConfigEntry,
  cartItems: any[],
  cartSpendCents: number
): boolean {
  const giftVid = String(cfg.variant_id);
  const quantityTrigger = parseTrigger(cfg);
  const priceTriggerCents = parsePriceTriggerCents(cfg);
  const collectionIds = collectionProductIdSet(cfg);

  const hasCollection = collectionIds.size > 0;
  const hasPriceTrigger = priceTriggerCents > 0;

  // A config needs at least one usable trigger to be actionable.
  if (!hasCollection && !hasPriceTrigger) {
    return false;
  }

  // Never add a gift to a cart with no paid (non-gift) spend.
  if (cartSpendCents <= 0) {
    return false;
  }

  // Price gate — the cart total must reach the trigger (equal or greater).
  // This is the authoritative gate for price-driven gifts, and is evaluated
  // independently of the collection set so it also governs auto-removal when
  // the cart total drops below the trigger.
  if (hasPriceTrigger && cartSpendCents < priceTriggerCents) {
    return false;
  }

  // Collection / quantity gate — only enforced when the trigger collection
  // actually resolved to a product set in the browser. Price-only configs, or
  // configs whose collection could not be enumerated (e.g. smart/large
  // collections), correctly fall back to the price gate above.
  if (hasCollection) {
    const qualifying = qualifyingQuantity(cartItems, giftVid, collectionIds);
    if (qualifying < quantityTrigger) {
      return false;
    }
  }

  return true;
}

/**
 * Gift lines to remove when ineligible. Prefer property-tagged lines; fall back
 * to the configured gift variant so we still clean up if props are missing.
 */
function giftLinesForRemoval(cartItems: any[], cfg: GwpConfigEntry): any[] {
  const tagged = giftLinesForConfig(cartItems, cfg);
  if (tagged.length > 0) {
    return tagged;
  }

  const giftVid = String(cfg.variant_id);
  return cartItems.filter((item) => {
    if (!item || String(item.variant_id) !== giftVid) {
      return false;
    }
    return (
      isFreeProductProperty(item.properties?.["_free-product"]) ||
      lineSpendCents(item) === 0
    );
  });
}

async function changeLineQuantity(
  lineKey: string,
  quantity: number
): Promise<any> {
  const res = await fetch("/cart/change.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: lineKey,
      quantity,
    }),
  });

  return res.json();
}

async function removeGiftLines(lines: any[]): Promise<any | null> {
  let lastCart: any = null;

  for (const line of lines) {
    const lineId = line?.key || line?.id;
    if (!lineId) {
      continue;
    }

    lastCart = await changeLineQuantity(String(lineId), 0);
    if (lastCart?.errors || lastCart?.status === 422 || lastCart?.message) {
      return null;
    }
  }

  return lastCart;
}

async function enforceSingleGiftLine(
  cfg: GwpConfigEntry,
  lines: any[]
): Promise<any | null> {
  if (lines.length === 0) {
    return null;
  }

  const [primaryLine, ...extraLines] = lines;
  let lastCart: any = null;

  if (extraLines.length > 0) {
    lastCart = await removeGiftLines(extraLines);
    if (lastCart == null) {
      return null;
    }
  }

  const primaryQty = Number(primaryLine.quantity) || 0;
  const primaryId = primaryLine.key || primaryLine.id;
  if (primaryQty !== GWP_MAX_LINE_QUANTITY && primaryId) {
    lastCart = await changeLineQuantity(
      String(primaryId),
      GWP_MAX_LINE_QUANTITY
    );
    if (lastCart?.errors || lastCart?.status === 422 || lastCart?.message) {
      return null;
    }
  }

  return lastCart;
}

const addingConfigKeys = new Set<string>();

async function syncOneConfig(
  cfg: GwpConfigEntry,
  cartItems: any[]
): Promise<boolean> {
  const giftVid = String(cfg.variant_id);
  const configKey = gwpConfigKey(cfg);
  const giftLines = giftLinesForRemoval(cartItems, cfg);
  const giftQty = totalGiftQuantity(giftLines);
  const cartSpendCents = cartSpendExcludingGiftsCents(cartItems);
  const eligible = qualifiesForGift(cfg, cartItems, cartSpendCents);

  if ((window as any).gwp_debug) {
    // Enable with `window.gwp_debug = true` in the console, then change the cart.
    console.debug("[gwp]", configKey, {
      giftVariant: giftVid,
      cartSpendCents,
      priceTriggerCents: parsePriceTriggerCents(cfg),
      quantityTrigger: parseTrigger(cfg),
      collectionSize: collectionProductIdSet(cfg).size,
      giftQtyInCart: giftQty,
      eligible,
    });
  }

  if (eligible) {
    if (giftQty === 0) {
      if (addingConfigKeys.has(configKey)) {
        return false;
      }

      addingConfigKeys.add(configKey);

      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [
              {
                id: giftVid,
                quantity: GWP_MAX_LINE_QUANTITY,
                properties: {
                  "_free-product": "1",
                  "_gwp_config_id": configKey,
                },
              },
            ],
          }),
        }).then((r) => r.json());

        if (res?.errors || res?.status === 422 || res?.message) {
          return false;
        }

        const fresh = await cart.get();
        if (!fresh?.items) {
          return true;
        }

        const updatedLines = giftLinesForRemoval(fresh.items, cfg);
        const updatedQty = totalGiftQuantity(updatedLines);

        if (updatedQty > GWP_MAX_LINE_QUANTITY || updatedLines.length > 1) {
          const reconciled = await enforceSingleGiftLine(cfg, updatedLines);
          return reconciled != null || updatedQty > 0;
        }

        return true;
      } finally {
        addingConfigKeys.delete(configKey);
      }
    }

    if (giftQty > GWP_MAX_LINE_QUANTITY || giftLines.length > 1) {
      const data = await enforceSingleGiftLine(cfg, giftLines);
      return data != null;
    }

    return false;
  }

  // Not eligible (qty and/or cart spend below triggers) — remove gift if present
  if (giftQty > 0) {
    const data = await removeGiftLines(giftLines);
    return data != null;
  }

  return false;
}

let debounceId: ReturnType<typeof setTimeout> | null = null;
let running = false;
let needsFollowUpSync = false;

export function scheduleGiftWithPurchaseSync(): void {
  if (debounceId) {
    clearTimeout(debounceId);
  }
  debounceId = setTimeout(() => {
    debounceId = null;
    void runGiftWithPurchaseSync();
  }, 120);
}

export async function runGiftWithPurchaseSync(): Promise<void> {
  if (isGwpCustomerExcluded()) {
    const cartData = await cart.get();
    if (!cartData || !Array.isArray(cartData.items)) {
      return;
    }

    const giftLines = cartData.items.filter((item: any) =>
      isFreeProductProperty(item?.properties?.["_free-product"])
    );

    if (giftLines.length === 0) {
      return;
    }

    const updatedCart = await removeGiftLines(giftLines);
    if (updatedCart) {
      store.refreshCart(updatedCart, { skipGwp: true });
      await nextTick();
      store.updateLastAdd(Date.now());
    }

    return;
  }

  const configs = getGwpConfigs();
  if (configs.length === 0) {
    return;
  }

  if (running) {
    needsFollowUpSync = true;
    return;
  }
  running = true;
  needsFollowUpSync = false;

  try {
    let cartData = await cart.get();
    if (!cartData || !Array.isArray(cartData.items)) {
      return;
    }

    let anyChanged = false;

    for (const cfg of configs) {
      const changed = await syncOneConfig(cfg, cartData.items);
      if (changed) {
        anyChanged = true;
        cartData = await cart.get();
        if (!cartData || !Array.isArray(cartData.items)) {
          break;
        }
      }
    }

    // Remove any orphaned gift lines whose GWP config is no longer present
    // (metaobject removed/renamed, or the gift variant became unavailable and
    // was filtered out of window.gwp_configs). Without this, a gift added by a
    // now-missing config lingers forever because the loop above never sees it.
    if (cartData && Array.isArray(cartData.items)) {
      const knownConfigKeys = new Set(configs.map((c) => gwpConfigKey(c)));
      const knownGiftVariantIds = new Set(
        configs.map((c) => String(c.variant_id))
      );

      const orphanGiftLines = cartData.items.filter((item: any) => {
        if (
          !item ||
          !isFreeProductProperty(item.properties?.["_free-product"])
        ) {
          return false;
        }
        const cfgId = item.properties?.["_gwp_config_id"];
        const cfgKnown =
          cfgId != null && cfgId !== "" && knownConfigKeys.has(String(cfgId));
        const variantKnown = knownGiftVariantIds.has(String(item.variant_id));
        return !cfgKnown && !variantKnown;
      });

      if (orphanGiftLines.length > 0) {
        const removed = await removeGiftLines(orphanGiftLines);
        if (removed) {
          anyChanged = true;
          cartData = await cart.get();
        }
      }
    }

    if (anyChanged && cartData) {
      store.refreshCart(cartData, { skipGwp: true });
      await nextTick();
      store.updateLastAdd(Date.now());
    }
  } finally {
    running = false;
    if (needsFollowUpSync) {
      needsFollowUpSync = false;
      scheduleGiftWithPurchaseSync();
    }
  }
}

export function initGiftWithPurchase(): void {
  document.addEventListener("product:added", () => {
    scheduleGiftWithPurchaseSync();
  });
  void runGiftWithPurchaseSync();
}
